<template>
  <DialogPortal>
    <DialogOverlay
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 data-[state=open]:animate-overlay-show"
    >
      <DialogContent as-child>
        <Box
          class="relative h-full w-full max-w-screen-2xl overflow-hidden bg-gradient-to-b from-[#77E9FF] to-white outline-none data-[state=open]:animate-content-show"
          rounded="lg"
          shadow="lg"
          border="lg"
        >
          <DialogTitle class="sr-only">Collective impact</DialogTitle>
          <DialogDescription class="sr-only">
            This shows the collective impact of the user and its impact family.
          </DialogDescription>
          <div
            v-if="isLoading"
            class="flex h-full w-full items-center justify-center"
          >
            Loading...
          </div>
          <div
            v-else-if="isError"
            class="flex h-full w-full items-center justify-center"
          >
            <button type="button" @click="() => refetch()">Retry</button>
          </div>
          <ClientOnly v-else-if="!!family">
            <ImpactFamilyViewer
              :offset-y="cardHeight"
              :empty-state="
                self && self.id === member?.id
                  ? { referralCode: self.referralCode }
                  : undefined
              "
              :family="family"
              class="h-full w-full"
            />
          </ClientOnly>

          <div class="absolute left-2 right-2 top-2 flex gap-3">
            <div
              class="flex flex-1 flex-col gap-3 rounded-xl border-2 border-navy bg-white p-3 lg:flex-row lg:items-center lg:gap-8"
            >
              <div
                class="flex items-center justify-between gap-2 lg:shrink-0 lg:flex-col lg:items-start"
              >
                <div
                  class="font-Kontesa text-[21px] font-extrabold uppercase leading-none tracking-tight lg:text-[32px]"
                >
                  <span v-if="self?.id === memberId">
                    My collective impact
                  </span>
                  <template v-else>
                    <span
                      v-if="isLoadingMember"
                      class="inline-block h-[21px] w-[190px] animate-pulse rounded bg-zinc-200"
                    />
                    <span v-else>
                      {{
                        member?.type === "member"
                          ? member.firstName
                          : member?.partner?.name
                      }}'s collective impact
                    </span>
                  </template>
                </div>
                <ProfileImpactCopyReferralButton
                  v-if="self?.id === memberId"
                  class="hidden !w-[190px] border border-navy !px-2 !py-1.5 shadow-[2px_2px_0px_0px] shadow-navy lg:block"
                  :referral-code="self.referralCode"
                />
                <DialogClose class="block shrink-0 lg:hidden">
                  <Box
                    class="flex h-10 w-10 items-center justify-center"
                    rounded="full"
                  >
                    <IconsClose class="h-4 w-4 stroke-[4]" />
                  </Box>
                </DialogClose>
              </div>

              <div
                ref="cardRef"
                class="grid grid-cols-2 justify-between gap-y-4 lg:flex lg:flex-1 lg:gap-x-3"
              >
                <div class="flex items-center gap-3">
                  <Box>
                    <ProfileImpactImage
                      class="xl:hidden"
                      size="medium"
                      :type="Campaign.TFTF"
                      colored
                    />
                    <ProfileImpactImage
                      class="hidden xl:flex"
                      size="large"
                      :type="Campaign.TFTF"
                      colored
                    />
                  </Box>
                  <div class="flex flex-col justify-between">
                    <span
                      v-if="!metrics"
                      class="h-[22px] animate-pulse rounded bg-zinc-200"
                    />
                    <span
                      v-else
                      class="font-Kontesa text-[22px] font-bold leading-none tracking-tighter lg:text-[24px] xl:text-[28px]"
                    >
                      {{ numberFormatter.format(metrics.trees) }}
                    </span>

                    <span
                      class="font-Aeonik text-[12px] leading-none tracking-tighter md:text-[14px]"
                    >
                      Trees planted
                    </span>
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <Box>
                    <ProfileImpactImage
                      class="xl:hidden"
                      size="medium"
                      :type="Campaign.PF"
                      colored
                    />
                    <ProfileImpactImage
                      class="hidden xl:flex"
                      size="large"
                      :type="Campaign.PF"
                      colored
                    />
                  </Box>
                  <div class="flex flex-col justify-between">
                    <span
                      v-if="!metrics"
                      class="h-[22px] animate-pulse rounded bg-zinc-200"
                    />
                    <span
                      v-else
                      class="font-Kontesa text-[22px] font-bold leading-none tracking-tighter lg:text-[24px] xl:text-[28px]"
                    >
                      {{ numberFormatter.format(metrics.ocean) }} lbs
                    </span>
                    <span
                      class="font-Aeonik text-[12px] leading-none tracking-tighter md:text-[14px]"
                    >
                      Plastic removed
                    </span>
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <Box>
                    <ProfileImpactImage
                      class="xl:hidden"
                      size="medium"
                      :type="Campaign.SARTHAK"
                      colored
                    />
                    <ProfileImpactImage
                      class="hidden xl:flex"
                      size="large"
                      :type="Campaign.SARTHAK"
                      colored
                    />
                  </Box>
                  <div class="flex flex-col justify-between">
                    <span
                      v-if="!metrics"
                      class="h-[22px] animate-pulse rounded bg-zinc-200"
                    />
                    <span
                      v-else
                      class="font-Kontesa text-[22px] font-bold leading-none tracking-tighter lg:text-[24px] xl:text-[28px]"
                    >
                      {{ numberFormatter.format(metrics.education) }} days
                    </span>
                    <span
                      class="font-Aeonik text-[12px] leading-none tracking-tighter md:text-[14px]"
                    >
                      Education funded
                    </span>
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <Box>
                    <ProfileImpactImage
                      class="xl:hidden"
                      size="medium"
                      :type="Campaign.REEFGEN"
                      colored
                    />
                    <ProfileImpactImage
                      class="hidden xl:flex"
                      size="large"
                      :type="Campaign.REEFGEN"
                      colored
                    />
                  </Box>
                  <div class="flex flex-col justify-between">
                    <span
                      v-if="!metrics"
                      class="h-[22px] animate-pulse rounded bg-zinc-200"
                    />
                    <span
                      v-else
                      class="font-Kontesa text-[22px] font-bold leading-none tracking-tighter lg:text-[24px] xl:text-[28px]"
                    >
                      {{ numberFormatter.format(metrics.seagrass) }}
                    </span>
                    <span
                      class="font-Aeonik text-[12px] leading-none tracking-tighter md:text-[14px]"
                    >
                      Seagrass seeds planted
                    </span>
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <Box>
                    <ProfileImpactImage
                      class="xl:hidden"
                      size="medium"
                      type="family"
                      colored
                    />
                    <ProfileImpactImage
                      class="hidden xl:flex"
                      size="large"
                      type="family"
                      colored
                    />
                  </Box>
                  <div class="flex flex-col justify-between">
                    <span
                      v-if="!metrics"
                      class="h-[22px] animate-pulse rounded bg-zinc-200"
                    />
                    <span
                      v-else
                      class="font-Kontesa text-[22px] font-bold leading-none tracking-tighter lg:text-[24px] xl:text-[28px]"
                    >
                      {{ numberFormatter.format(metrics.family) }}
                    </span>
                    <span
                      class="font-Aeonik text-[12px] leading-none tracking-tighter md:text-[14px]"
                    >
                      Members
                    </span>
                  </div>
                </div>
              </div>

              <ProfileImpactCopyReferralButton
                v-if="self?.id === memberId"
                class="mt-2 border border-navy !py-1.5 shadow-[2px_2px_0px_0px] shadow-navy lg:hidden"
                :referral-code="self.referralCode"
              />
            </div>
            <DialogClose
              class="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-navy bg-white lg:flex"
            >
              <IconsClose class="h-4 w-4 stroke-[4]" />
            </DialogClose>
          </div>
        </Box>
      </DialogContent>
    </DialogOverlay>
  </DialogPortal>
</template>

<script setup lang="ts">
import { useQuery } from "@tanstack/vue-query";
import { memberQueries } from "~/domains/member/queries";
import { Campaign } from "~/lib/campaigns";

const props = defineProps<{ memberId: number }>();
const memberId = toRef(props, "memberId");
const {
  data: family,
  isLoading,
  isError,
  refetch,
} = useQuery(memberQueries.family(memberId));

const { data: member, isLoading: isLoadingMember } = useQuery(
  memberQueries.asGuest(memberId),
);
const { data: self } = useQuery(memberQueries.self());

const metrics = computed(() => {
  if (!family.value) return;

  const metrics = {
    ocean: 0,
    education: 0,
    trees: 0,
    seagrass: 0,
    family: 0,
    impact: 0,
  };

  const stack = [
    {
      impact: family.value.points,
      metrics: family.value.metrics,
      family: family.value.family,
    },
  ];

  while (stack.length) {
    const next = stack.pop()!;

    metrics.impact += next.impact;
    metrics.family += next.family.length;

    for (const item of next.metrics) {
      switch (item.campaignId) {
        case Campaign.TFTF:
          metrics.trees += item.points;
          break;
        case Campaign.OVI:
        case Campaign.PF:
        case Campaign.CLEANHUB:
          metrics.ocean += item.points;
          break;
        case Campaign.SARTHAK:
          metrics.education += item.points;
          break;
        case Campaign.REEFGEN:
          metrics.seagrass += item.points;
          break;
      }
    }

    stack.push(
      ...next.family.map((referral) => ({
        impact: referral.points,
        metrics: referral.metrics,
        family: referral.family,
      })),
    );
  }

  return metrics;
});

const cardRef = ref<HTMLDivElement | null>(null);
const { height: cardHeight } = useElementSize(cardRef);

const numberFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  notation: "compact",
  maximumFractionDigits: 2,
});
</script>
